import React, { useCallback, useMemo, useEffect, useState } from "react";
import { folderManagementServices } from "../../APIServices/folderManagementServices";

import AddNewFolder from "./AddNewFolder";
import AddNewSubFolder from "./AddNewSubFolder";
import UpdateFolder from "./UpdateFolder";
import DeleteFolder from "./DeleteFolder";

const FolderNode = React.memo(function FolderNode({
  node,
  level = 0,
  setModalOpen,
  setFolderData,
}) {
  const [open, setOpen] = useState(true);

  const hasChildren =
    Array.isArray(node.children) && node.children.length > 0;

  const toggleFolder = () => {
    if (hasChildren) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <>
      <div className="folder-row">
        <div
          className="folder-name"
          style={{
            paddingLeft: `${level * 24}px`,
            cursor: "pointer"
          }}
          onClick={toggleFolder}
        >
          {hasChildren ? (
            <i
              className={`ti ${open
                  ? "ti-chevron-down"
                  : "ti-chevron-right"
                }`}
            />
          ) : (
            <div
              style={{
                width: 18,
              }}
            />
          )}

          <i
            className={`ti ${hasChildren
                ? open
                  ? "ti-folder-open"
                  : "ti-folder"
                : "ti-folder"
              }`}
          />

          <div>
            <div style={{ fontWeight: 600 }}>
              {node.code ? `${node.code} · ${node.name}` : node.name}
            </div>
            {node.description ? (
              <div className="xs muted" style={{ marginTop: 2 }}>
                {node.description}
              </div>
            ) : null}
          </div>
        </div>

        <div className="folder-actions">
          <button
            className="icon-btn"
            title="Add Folder"
            style={{
              color: "var(--ok)",
            }}
            onClick={() => {
              setFolderData(node);
              setModalOpen("addSubFolder");
            }}
          >
            <i className="ti ti-plus" />
          </button>

          <button
            className="icon-btn"
            title="Edit Folder"
            style={{
              color: "var(--info)",
            }}
            onClick={() => {
              setFolderData(node);
              setModalOpen("update");
            }}
          >
            <i className="ti ti-pencil" />
          </button>

          <button
            className="icon-btn"
            title="Delete Folder"
            style={{
              color: "var(--danger)",
            }}
            onClick={() => {
              setFolderData(node);
              setModalOpen("delete");
            }}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      </div>

      {open &&
        hasChildren &&
        node?.children.map((child) => (
          <FolderNode
            key={child.uniqueId}
            node={child}
            level={level + 1}
            setFolderData={setFolderData}
            setModalOpen={setModalOpen}
          />
        ))}
    </>
  );
});

export default function FolderManagement() {
  const [folderList, setFolderList] = useState([]);
  const [folderData, setFolderData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const getFolderManagementData = useCallback(async () => {
    try {
      setLoading(true);

      const response =
        await folderManagementServices.getFolderManagementData();

      setFolderList(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error(err);
      setFolderList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const buildFolderTree = useCallback((folders = []) => {
    const lookup = new Map();

    folders.forEach((item) => {
      lookup.set(item.uniqueId, {
        ...item,
        children: [],
      });
    });

    const roots = [];

    folders.forEach((item) => {
      const current = lookup.get(item.uniqueId);

      if (
        item.parentId &&
        lookup.has(item.parentId)
      ) {
        lookup
          .get(item.parentId)
          .children.push(current);
      } else {
        roots.push(current);
      }
    });

    return roots;
  }, []);

  const folderTree = useMemo(() => {
    return buildFolderTree(folderList);
  }, [folderList, buildFolderTree]);

  useEffect(() => {
    getFolderManagementData();
  }, [getFolderManagementData]);

  return (
    <>
      <div className="page-container">
        <div className="page-header">
          <div>
            <div className="page-eyebrow">
              Document Management
            </div>

            <h1 className="page-title">
              Folder Management
            </h1>

            <p className="page-sub">
              Organize folders and subfolders in a hierarchical structure.
              Create, edit and manage parent and child folders.
            </p>
          </div>
        </div>

        <div className="flex between center mb-3">
          <div className="section-label">
            Folder Structure

            <span className="count">
              {folderList.length}
            </span>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => {
              setFolderData(null);
              setModalOpen("add");
            }}
          >
            <i className="ti ti-plus" />
            Add Root Folder
          </button>
        </div>

        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
              }}
            >
              <div className="buttonLoader" />

              <div
                style={{
                  marginTop: 12,
                }}
              >
                Loading folders...
              </div>
            </div>
          ) : folderTree.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "var(--ink-3)",
              }}
            >
              <i
                className="ti ti-folder"
                style={{
                  fontSize: 42,
                  marginBottom: 10,
                  display: "block",
                }}
              />

              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                No Folder Found
              </div>

              <div>
                Click
                <strong> Add Root Folder </strong>
                to create your first folder.
              </div>
            </div>
          ) : (
            <>
              {folderTree.map((folder) => (
                <FolderNode
                  key={folder.uniqueId}
                  node={folder}
                  level={0}
                  setFolderData={setFolderData}
                  setModalOpen={setModalOpen}
                />
              ))}
            </>
          )}
        </div>
      </div>
      {modalOpen === "add" && (
        <AddNewFolder
          setModalOpen={setModalOpen}
          getFolderManagementData={getFolderManagementData}
        />
      )}

      {modalOpen === "addSubFolder" && (
        <AddNewSubFolder
          setModalOpen={setModalOpen}
          getFolderManagementData={getFolderManagementData}
          folderData={folderData}
        />
      )}

      {modalOpen === "update" && (
        <UpdateFolder
          folderData={folderData}
          setModalOpen={setModalOpen}
          getFolderManagementData={getFolderManagementData}
          confirm={confirm}
          setConfirm={setConfirm}
        />
      )}

      {modalOpen === "delete" && (
        <DeleteFolder
          folderData={folderData}
          setModalOpen={setModalOpen}
          getFolderManagementData={getFolderManagementData}
        />
      )}
    </>
  );
}